import type { FC } from 'react';

import Pagination from '@/common-components/Pagination';

import PaymentDetailDrawer from '../components/PaymentDetailDrawer';
import PaymentsExportBar from '../components/PaymentsExportBar';
import PaymentsSummaryCards from '../components/PaymentsSummaryCards';
import PaymentsTable from '../components/PaymentsTable';
import PaymentsToolbar from '../components/PaymentsToolbar';
import { usePaymentsPage } from '../hooks/usePaymentsPage.hook';

const PaymentsPage: FC = () => {
  const vm = usePaymentsPage();

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <div>
        <h1 className="text-[20px] font-bold text-[var(--color-ink)]">Payments &amp; Revenue</h1>
        <p className="text-[13px] text-[var(--color-ink-muted)]">
          Verify borrower payments, settle owners, and refund deposits.
        </p>
      </div>

      <PaymentsSummaryCards summary={vm.summary} />

      <section className="rounded-2xl border border-[var(--color-divider)] bg-white">
        <PaymentsToolbar
          tab={vm.tab}
          search={vm.search}
          summary={vm.summary}
          onChangeTab={vm.onChangeTab}
          onChangeSearch={vm.onChangeSearch}
        />

        <PaymentsExportBar
          from={vm.from}
          to={vm.to}
          basis={vm.basis}
          isExporting={vm.isExporting}
          exportError={vm.exportError}
          rangeErrors={vm.rangeErrors}
          onChangeFrom={vm.onChangeFrom}
          onChangeTo={vm.onChangeTo}
          onChangeBasis={vm.onChangeBasis}
          onExport={vm.onExport}
        />

        {vm.isLoading ? (
          <p className="p-8 text-center text-[13px] text-[var(--color-ink-muted)]">Loading…</p>
        ) : vm.isError ? (
          <p className="p-8 text-center text-[13px] text-red-600">
            {vm.errorMessage ?? 'Could not load payments.'}
          </p>
        ) : vm.records.length === 0 ? (
          <p className="p-8 text-center text-[13px] text-[var(--color-ink-muted)]">
            No payments in this queue.
          </p>
        ) : (
          <PaymentsTable records={vm.records} onSelect={vm.onSelect} />
        )}

        <div className="flex items-center justify-between border-t border-[var(--color-divider)] p-4">
          <span className="text-[12px] text-[var(--color-ink-muted)]">{vm.total} total</span>
          <Pagination page={vm.page} totalPages={vm.totalPages} onChange={vm.onChangePage} />
        </div>
      </section>

      {vm.selected ? (
        <PaymentDetailDrawer
          record={vm.selected}
          isMutating={vm.mutatingId === vm.selected.id}
          actionError={vm.actionError}
          openForm={vm.openForm}
          draft={vm.draft}
          onClose={vm.onCloseDrawer}
          onOpenForm={vm.onOpenForm}
          onDraftChange={vm.onDraftChange}
          onVerify={vm.onVerify}
          onReject={vm.onReject}
          onSettle={vm.onSettle}
          onRefundDeposit={vm.onRefundDeposit}
          onConfirmRefund={vm.onConfirmRefund}
          onNoPaymentReceived={vm.onNoPaymentReceived}
          onRequestPayoutDetails={vm.onRequestPayoutDetails}
          copiedKey={vm.copiedKey}
          onCopy={vm.onCopy}
        />
      ) : null}
    </div>
  );
};

export default PaymentsPage;
